import { useEffect } from "react";
import { X, Download, ExternalLink, FileText } from "lucide-react";
import { fileUrl } from "@/lib/api";

/**
 * Right-side pane that previews an attachment next to the note. PDFs and
 * plain-text/CSV/JSON preview inline via an iframe (same-origin served via
 * our /api/files/{path}). Office formats fall back to a tidy card with
 * Open-in-new-tab + Download buttons because browsers can't render those
 * without plugins.
 */
export default function AttachmentPreview({ attachment, onClose }) {
  useEffect(() => {
    if (!attachment) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [attachment, onClose]);

  if (!attachment) return null;
  const ct = (attachment.content_type || "").toLowerCase();
  const url = fileUrl(attachment.path);
  const isAudio = ct.startsWith("audio/");
  const canInline = ct === "application/pdf" || ct.startsWith("text/") || ct === "application/json" || ct === "application/xml";

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[65] bg-black/70 flex items-stretch justify-end"
      onClick={onClose}
      data-testid="attachment-preview"
    >
      <div
        className="w-full sm:w-[640px] max-w-full bg-white dark:bg-stone-900 shadow-2xl flex flex-col h-full"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-3 px-4 py-3 border-b border-stone-200 dark:border-stone-700 shrink-0">
          <FileText className="h-4 w-4 text-stone-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate" data-testid="preview-filename">{attachment.filename}</div>
            <div className="text-xs text-stone-500 truncate">{ct || "file"}</div>
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300"
            title="Open in new tab"
            data-testid="preview-open-new-tab"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
          <a
            href={url}
            download={attachment.filename}
            className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300"
            title="Download"
            data-testid="preview-download"
          >
            <Download className="h-4 w-4" />
          </a>
          <button
            onClick={onClose}
            className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300"
            data-testid="preview-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 min-h-0 bg-stone-100 dark:bg-stone-950">
          {isAudio ? (
            <div className="h-full flex flex-col items-center justify-center gap-4 px-6">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                <FileText className="h-9 w-9 text-white" />
              </div>
              <div className="text-sm font-medium text-stone-700 dark:text-stone-200">{attachment.filename}</div>
              <audio controls src={url} className="w-full max-w-sm" data-testid="preview-audio" />
            </div>
          ) : canInline ? (
            <iframe
              src={url}
              title={attachment.filename}
              className="w-full h-full border-0"
              data-testid="preview-iframe"
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center px-6 gap-3 text-stone-500">
              <FileText className="h-12 w-12 opacity-40" />
              <div className="text-sm">Preview isn't available for <code className="font-mono text-xs bg-stone-200 dark:bg-stone-800 px-1 py-0.5 rounded">{ct || "this type"}</code>.</div>
              <div className="flex gap-2">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-900 text-white text-xs font-medium hover:bg-stone-800 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-200"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Open in new tab
                </a>
                <a
                  href={url}
                  download={attachment.filename}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-300 dark:border-stone-700 text-xs font-medium hover:bg-stone-100 dark:hover:bg-stone-800"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
